/**
 * Parametric Engine - BIM 2025 Grade
 * 
 * Formula-driven parametric constraint system with dependency tracking,
 * formula evaluation, and automatic regeneration.
 */

import { v4 as uuidv4 } from 'uuid';
import { BIMElement, Project } from './types';
import { DependencyGraph } from './DependencyGraph';

// ==================== Parameter System ====================

export type ParameterType =
    | 'Length'
    | 'Area'
    | 'Volume'
    | 'Angle'
    | 'Number'
    | 'Integer'
    | 'Text'
    | 'YesNo'
    | 'Material'
    | 'FamilyType'
    | 'ElementId';

export type ParameterGroup =
    | 'Dimensions'
    | 'Identity Data'
    | 'Constraints'
    | 'Structural'
    | 'Mechanical'
    | 'Electrical'
    | 'Other';

export interface ParameterDefinition {
    id: string;
    name: string;
    type: ParameterType;
    group: ParameterGroup;
    isInstance: boolean; // Instance vs Type parameter
    isReporting: boolean; // Calculated, read-only
    isShared: boolean; // Shared across families
    formula?: string; // If formula-driven
    defaultValue?: ParameterValue;
    builtIn?: boolean; // System parameter
    tooltip?: string;
    units?: string; // Display units
}

export type ParameterValue = number | string | boolean | null;

export interface ParameterInstance {
    definitionId: string;
    value: ParameterValue;
    overridden: boolean; // Differs from type value
}

// ==================== Formula System ====================

export interface FormulaToken {
    type: 'NUMBER' | 'STRING' | 'PARAMETER' | 'OPERATOR' | 'FUNCTION' | 'PAREN';
    value: string | number;
    position: number;
}

export interface FormulaAST {
    type: 'BINARY' | 'UNARY' | 'LITERAL' | 'PARAMETER' | 'FUNCTION' | 'CONDITIONAL';
    operator?: string;
    left?: FormulaAST;
    right?: FormulaAST;
    value?: ParameterValue;
    parameterName?: string;
    functionName?: string;
    arguments?: FormulaAST[];
    condition?: FormulaAST;
    thenBranch?: FormulaAST;
    elseBranch?: FormulaAST;
}

export class FormulaParser {
    private tokens: FormulaToken[] = [];
    private current: number = 0;

    /**
     * Parse a formula string into an AST
     * Supports: +, -, *, /, ^, (, ), IF, AND, OR, NOT, sin, cos, tan, sqrt, abs, round, min, max
     */
    parse(formula: string): FormulaAST {
        this.tokens = this.tokenize(formula);
        this.current = 0;
        return this.parseExpression();
    }

    private tokenize(formula: string): FormulaToken[] {
        const tokens: FormulaToken[] = [];
        let i = 0;

        while (i < formula.length) {
            const char = formula[i];

            // Skip whitespace
            if (/\s/.test(char)) {
                i++;
                continue;
            }

            // Numbers (including decimals)
            if (/[0-9.]/.test(char)) {
                let num = '';
                while (i < formula.length && /[0-9.]/.test(formula[i])) {
                    num += formula[i++];
                }
                tokens.push({ type: 'NUMBER', value: parseFloat(num), position: i });
                continue;
            }

            // Strings (quoted)
            if (char === '"' || char === "'") {
                const quote = char;
                i++;
                let str = '';
                while (i < formula.length && formula[i] !== quote) {
                    str += formula[i++];
                }
                i++; // Skip closing quote
                tokens.push({ type: 'STRING', value: str, position: i });
                continue;
            }

            // Operators
            if (/[+\-*^<>=!]|\//.test(char)) {
                let op = char;
                i++;
                // Check for multi-char operators (<=, >=, ==, !=)
                if (i < formula.length && formula[i] === '=') {
                    op += formula[i++];
                }
                tokens.push({ type: 'OPERATOR', value: op, position: i });
                continue;
            }

            // Parentheses and comma
            if (/[(),]/.test(char)) {
                tokens.push({ type: 'PAREN', value: char, position: i });
                i++;
                continue;
            }

            // Identifiers (parameters and functions)
            if (/[a-zA-Z_]/.test(char)) {
                let identifier = '';
                while (i < formula.length && /[a-zA-Z0-9_]/.test(formula[i])) {
                    identifier += formula[i++];
                }

                // Check if it's a function
                const functions = ['IF', 'AND', 'OR', 'NOT', 'sin', 'cos', 'tan', 'sqrt', 'abs', 'round', 'min', 'max', 'floor', 'ceiling', 'pow', 'log', 'exp'];
                if (functions.includes(identifier.toLowerCase()) || functions.includes(identifier.toUpperCase())) {
                    tokens.push({ type: 'FUNCTION', value: identifier.toLowerCase(), position: i });
                } else {
                    tokens.push({ type: 'PARAMETER', value: identifier, position: i });
                }
                continue;
            }

            // Unknown character
            i++;
        }

        return tokens;
    }

    private parseExpression(): FormulaAST {
        return this.parseConditional();
    }

    private parseConditional(): FormulaAST {
        // Check for IF function
        if (this.match('FUNCTION', 'if')) {
            this.consume('PAREN', '(');
            const condition = this.parseOr();
            this.consume('PAREN', ',');
            const thenBranch = this.parseOr();
            this.consume('PAREN', ',');
            const elseBranch = this.parseOr();
            this.consume('PAREN', ')');
            return {
                type: 'CONDITIONAL',
                condition,
                thenBranch,
                elseBranch
            };
        }
        return this.parseOr();
    }

    private parseOr(): FormulaAST {
        let left = this.parseAnd();

        while (this.match('FUNCTION', 'or') || this.match('OPERATOR', '||')) {
            const right = this.parseAnd();
            left = { type: 'BINARY', operator: 'or', left, right };
        }

        return left;
    }

    private parseAnd(): FormulaAST {
        let left = this.parseEquality();

        while (this.match('FUNCTION', 'and') || this.match('OPERATOR', '&&')) {
            const right = this.parseEquality();
            left = { type: 'BINARY', operator: 'and', left, right };
        }

        return left;
    }

    private parseEquality(): FormulaAST {
        let left = this.parseComparison();

        while (this.matchAny('OPERATOR', ['==', '!=', '='])) {
            const operator = this.previous().value as string;
            const right = this.parseComparison();
            left = { type: 'BINARY', operator: operator === '=' ? '==' : operator, left, right };
        }

        return left;
    }

    private parseComparison(): FormulaAST {
        let left = this.parseAddition();

        while (this.matchAny('OPERATOR', ['<', '>', '<=', '>='])) {
            const operator = this.previous().value as string;
            const right = this.parseAddition();
            left = { type: 'BINARY', operator, left, right };
        }

        return left;
    }

    private parseAddition(): FormulaAST {
        let left = this.parseMultiplication();

        while (this.matchAny('OPERATOR', ['+', '-'])) {
            const operator = this.previous().value as string;
            const right = this.parseMultiplication();
            left = { type: 'BINARY', operator, left, right };
        }

        return left;
    }

    private parseMultiplication(): FormulaAST {
        let left = this.parsePower();

        while (this.matchAny('OPERATOR', ['*', '/'])) {
            const operator = this.previous().value as string;
            const right = this.parsePower();
            left = { type: 'BINARY', operator, left, right };
        }

        return left;
    }

    private parsePower(): FormulaAST {
        let left = this.parseUnary();

        while (this.match('OPERATOR', '^')) {
            const right = this.parseUnary();
            left = { type: 'BINARY', operator: '^', left, right };
        }

        return left;
    }

    private parseUnary(): FormulaAST {
        if (this.match('OPERATOR', '-')) {
            const right = this.parseUnary();
            return { type: 'UNARY', operator: '-', right };
        }
        if (this.match('FUNCTION', 'not') || this.match('OPERATOR', '!')) {
            const right = this.parseUnary();
            return { type: 'UNARY', operator: 'not', right };
        }
        return this.parseCall();
    }

    private parseCall(): FormulaAST {
        if (this.check('FUNCTION')) {
            const funcToken = this.advance();
            const funcName = funcToken.value as string;

            if (funcName === 'if') {
                // Already handled in parseConditional
                this.current--;
                return this.parsePrimary();
            }

            this.consume('PAREN', '(');
            const args: FormulaAST[] = [];

            if (!this.check('PAREN') || this.peek().value !== ')') {
                do {
                    args.push(this.parseExpression());
                } while (this.match('PAREN', ','));
            }

            this.consume('PAREN', ')');

            return {
                type: 'FUNCTION',
                functionName: funcName,
                arguments: args
            };
        }

        return this.parsePrimary();
    }

    private parsePrimary(): FormulaAST {
        if (this.check('NUMBER')) {
            return { type: 'LITERAL', value: this.advance().value as number };
        }

        if (this.check('STRING')) {
            return { type: 'LITERAL', value: this.advance().value as string };
        }

        if (this.check('PARAMETER')) {
            return { type: 'PARAMETER', parameterName: this.advance().value as string };
        }

        if (this.match('PAREN', '(')) {
            const expr = this.parseExpression();
            this.consume('PAREN', ')');
            return expr;
        }

        throw new Error(`Unexpected token at position ${this.current}`);
    }

    private match(type: FormulaToken['type'], value?: string | number): boolean {
        if (this.check(type) && (value === undefined || this.peek().value === value)) {
            this.advance();
            return true;
        }
        return false;
    }

    private matchAny(type: FormulaToken['type'], values: (string | number)[]): boolean {
        for (const value of values) {
            if (this.match(type, value)) return true;
        }
        return false;
    }

    private check(type: FormulaToken['type']): boolean {
        return !this.isAtEnd() && this.peek().type === type;
    }

    private advance(): FormulaToken {
        if (!this.isAtEnd()) this.current++;
        return this.previous();
    }

    private consume(type: FormulaToken['type'], value: string | number): FormulaToken {
        if (this.check(type) && this.peek().value === value) {
            return this.advance();
        }
        throw new Error(`Expected ${value} at position ${this.current}`);
    }

    private peek(): FormulaToken {
        return this.tokens[this.current];
    }

    private previous(): FormulaToken {
        return this.tokens[this.current - 1];
    }

    private isAtEnd(): boolean {
        return this.current >= this.tokens.length;
    }
}

export class FormulaEvaluator {
    /**
     * Evaluate a formula AST with given parameter values
     */
    /**
     * Evaluate a formula AST with given parameter values
     */
    evaluate(ast: FormulaAST, parameters: Map<string, ParameterValue>, globalParameters?: Map<string, ParameterValue>): ParameterValue {
        switch (ast.type) {
            case 'LITERAL':
                return ast.value!;

            case 'PARAMETER': {
                // Check local parameters first
                let value = parameters.get(ast.parameterName!);

                // If not found, check global parameters
                if (value === undefined && globalParameters) {
                    value = globalParameters.get(ast.parameterName!);
                }

                if (value === undefined) {
                    throw new Error(`Unknown parameter: ${ast.parameterName}`);
                }
                return value;
            }

            case 'UNARY': {
                const right = this.evaluate(ast.right!, parameters, globalParameters);
                switch (ast.operator) {
                    case '-': return -(right as number);
                    case 'not': return !right;
                    default: throw new Error(`Unknown unary operator: ${ast.operator}`);
                }
            }

            case 'BINARY': {
                const left = this.evaluate(ast.left!, parameters, globalParameters);
                const right = this.evaluate(ast.right!, parameters, globalParameters);
                return this.evaluateBinary(ast.operator!, left, right);
            }

            case 'FUNCTION': {
                const args = ast.arguments!.map(arg => this.evaluate(arg, parameters, globalParameters));
                return this.evaluateFunction(ast.functionName!, args);
            }

            case 'CONDITIONAL': {
                const condition = this.evaluate(ast.condition!, parameters, globalParameters);
                if (condition) {
                    return this.evaluate(ast.thenBranch!, parameters, globalParameters);
                } else {
                    return this.evaluate(ast.elseBranch!, parameters, globalParameters);
                }
            }

            default:
                throw new Error(`Unknown AST node type: ${ast.type}`);
        }
    }

    private evaluateBinary(operator: string, left: ParameterValue, right: ParameterValue): ParameterValue {
        const l = left as number;
        const r = right as number;

        switch (operator) {
            case '+': return l + r;
            case '-': return l - r;
            case '*': return l * r;
            case '/': return r !== 0 ? l / r : 0;
            case '^': return Math.pow(l, r);
            case '<': return l < r;
            case '>': return l > r;
            case '<=': return l <= r;
            case '>=': return l >= r;
            case '==': return left === right;
            case '!=': return left !== right;
            case 'and': return Boolean(left) && Boolean(right);
            case 'or': return Boolean(left) || Boolean(right);
            default: throw new Error(`Unknown binary operator: ${operator}`);
        }
    }

    private evaluateFunction(name: string, args: ParameterValue[]): ParameterValue {
        switch (name) {
            case 'sin': return Math.sin(args[0] as number);
            case 'cos': return Math.cos(args[0] as number);
            case 'tan': return Math.tan(args[0] as number);
            case 'sqrt': return Math.sqrt(args[0] as number);
            case 'abs': return Math.abs(args[0] as number);
            case 'round': return Math.round(args[0] as number);
            case 'floor': return Math.floor(args[0] as number);
            case 'ceiling': return Math.ceil(args[0] as number);
            case 'min': return Math.min(...args.map(a => a as number));
            case 'max': return Math.max(...args.map(a => a as number));
            case 'pow': return Math.pow(args[0] as number, args[1] as number);
            case 'log': return Math.log(args[0] as number);
            case 'exp': return Math.exp(args[0] as number);
            default: throw new Error(`Unknown function: ${name}`);
        }
    }
}

// DependencyGraph moved to ./DependencyGraph.ts

// ==================== Parametric Engine ====================

export class ParametricEngine {
    private definitions: Map<string, ParameterDefinition> = new Map();
    private dependencyGraph: DependencyGraph = new DependencyGraph();
    private formulaParser: FormulaParser = new FormulaParser();
    private formulaEvaluator: FormulaEvaluator = new FormulaEvaluator();
    private parsedFormulas: Map<string, FormulaAST> = new Map(); // Cache parsed formulas

    constructor() {
        this.registerBuiltInParameters();
    }

    private registerBuiltInParameters(): void {
        // ... (Existing code)
        // Register common built-in parameters
        const builtIns: Omit<ParameterDefinition, 'id'>[] = [
            // Dimensions
            { name: 'Length', type: 'Length', group: 'Dimensions', isInstance: true, isReporting: true, isShared: false, builtIn: true },
            { name: 'Width', type: 'Length', group: 'Dimensions', isInstance: false, isReporting: false, isShared: false, builtIn: true },
            { name: 'Height', type: 'Length', group: 'Dimensions', isInstance: false, isReporting: false, isShared: false, builtIn: true },
            { name: 'Depth', type: 'Length', group: 'Dimensions', isInstance: false, isReporting: false, isShared: false, builtIn: true },
            { name: 'Thickness', type: 'Length', group: 'Dimensions', isInstance: false, isReporting: false, isShared: false, builtIn: true },
            { name: 'Area', type: 'Area', group: 'Dimensions', isInstance: true, isReporting: true, isShared: false, builtIn: true },
            { name: 'Volume', type: 'Volume', group: 'Dimensions', isInstance: true, isReporting: true, isShared: false, builtIn: true },
            { name: 'Perimeter', type: 'Length', group: 'Dimensions', isInstance: true, isReporting: true, isShared: false, builtIn: true },

            // Constraints
            { name: 'Base Level', type: 'ElementId', group: 'Constraints', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Top Level', type: 'ElementId', group: 'Constraints', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Base Offset', type: 'Length', group: 'Constraints', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Top Offset', type: 'Length', group: 'Constraints', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Host', type: 'ElementId', group: 'Constraints', isInstance: true, isReporting: true, isShared: false, builtIn: true },

            // Structural
            { name: 'Structural Usage', type: 'Text', group: 'Structural', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Structural Material', type: 'Material', group: 'Structural', isInstance: false, isReporting: false, isShared: false, builtIn: true },
            { name: 'Cut Length', type: 'Length', group: 'Structural', isInstance: true, isReporting: true, isShared: false, builtIn: true },

            // Identity
            { name: 'Mark', type: 'Text', group: 'Identity Data', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Comments', type: 'Text', group: 'Identity Data', isInstance: true, isReporting: false, isShared: false, builtIn: true },
            { name: 'Type Name', type: 'Text', group: 'Identity Data', isInstance: false, isReporting: true, isShared: false, builtIn: true },
            { name: 'Family Name', type: 'Text', group: 'Identity Data', isInstance: false, isReporting: true, isShared: false, builtIn: true },
        ];

        builtIns.forEach(def => {
            this.definitions.set(def.name, { ...def, id: uuidv4() });
        });
    }

    /**
     * Register a new parameter definition
     */
    registerParameter(definition: Omit<ParameterDefinition, 'id'>): ParameterDefinition {
        const fullDef: ParameterDefinition = { ...definition, id: uuidv4() };
        this.definitions.set(definition.name, fullDef);
        return fullDef;
    }

    /**
     * Set a formula for a parameter on an element
     * If globalParameters are provided, dependencies can be checked against them
     */
    setFormula(elementId: string, parameterName: string, formula: string, project?: Project): void {
        const key = `${elementId}:${parameterName}`;

        // Parse and cache the formula
        const ast = this.formulaParser.parse(formula);
        this.parsedFormulas.set(key, ast);

        // Extract parameter references and update dependency graph
        this.dependencyGraph.clearDependenciesForParameter(elementId, parameterName);
        this.extractDependencies(elementId, parameterName, ast, project);

        // Check for circular dependencies
        if (this.dependencyGraph.hasCircularDependency(elementId, parameterName)) {
            this.parsedFormulas.delete(key);
            throw new Error(`Circular dependency detected for ${parameterName}`);
        }
    }

    private extractDependencies(elementId: string, parameterName: string, ast: FormulaAST, project?: Project): void {
        const visit = (node: FormulaAST) => {
            if (node.type === 'PARAMETER') {
                const depName = node.parameterName!;
                // Determine if it's a global dependency
                let targetElementId = elementId;

                // If the parameter is NOT in the element's parameter list but IS in Global Parameters
                // We consider it a global dependency.
                // However, setFormula often happens before execution.
                // Simple heuristic: If project has global param and element doesn't have local param (or we assume override).
                // For now, simpler: If project.globalParameters has it, we link to GLOBAL. 
                // BUT, local parameters shadow globals.

                if (project && project.globalParameters && project.globalParameters.has(depName)) {
                    // Check if local element shadows it?
                    const element = project.elements.get(elementId);
                    if (element && !element.parameters[depName]) {
                        targetElementId = 'GLOBAL_PROJECT_PARAMS';
                    }
                }

                this.dependencyGraph.addDependency(
                    elementId,
                    parameterName,
                    targetElementId,
                    depName
                );
            }

            if (node.left) visit(node.left);
            if (node.right) visit(node.right);
            if (node.condition) visit(node.condition);
            if (node.thenBranch) visit(node.thenBranch);
            if (node.elseBranch) visit(node.elseBranch);
            if (node.arguments) node.arguments.forEach(visit);
        };

        visit(ast);
    }

    /**
     * Regenerate parameters after a change.
     * 
     * If elementId is 'GLOBAL_PROJECT_PARAMS', it regenerates all elements dependent on the changed global parameter.
     * Returns a Map where Key = Element ID, Value = Map of changed Parameter Names -> Values.
     */
    regenerate(project: Project, elementId: string, changedParameter: string): Map<string, Map<string, ParameterValue>> {
        const batchResults = new Map<string, Map<string, ParameterValue>>();

        // Helper to record result
        const addResult = (elemId: string, paramName: string, val: ParameterValue) => {
            if (!batchResults.has(elemId)) {
                batchResults.set(elemId, new Map());
            }
            batchResults.get(elemId)!.set(paramName, val);
        };

        const affected = this.dependencyGraph.getAffectedParameters(elementId, changedParameter);

        // Group by element to minimize lookups
        const affectedElements = new Set<string>();
        // Map "elementId:paramName" -> paramName list for optimization
        const elementParamsToUpdate = new Map<string, string[]>();

        for (const key of affected) {
            const [eId, pName] = key.split(':');
            affectedElements.add(eId);
            if (!elementParamsToUpdate.has(eId)) elementParamsToUpdate.set(eId, []);
            elementParamsToUpdate.get(eId)!.push(pName);
        }

        // For each affected element, re-evaluate parameters
        for (const elemId of affectedElements) {
            const element = project.elements.get(elemId);
            if (!element) continue;

            const currentValues = new Map<string, ParameterValue>();
            Object.entries(element.parameters).forEach(([key, value]) => {
                currentValues.set(key, value);
            });

            const paramsToUpdate = elementParamsToUpdate.get(elemId) || [];

            // Re-evaluate
            for (const pName of paramsToUpdate) {
                const formulaKey = `${elemId}:${pName}`;
                const ast = this.parsedFormulas.get(formulaKey);

                if (ast) {
                    try {
                        const newValue = this.formulaEvaluator.evaluate(ast, currentValues, project.globalParameters);

                        // Update local map for subsequent formulas in this chain
                        currentValues.set(pName, newValue);

                        // Add to batch results
                        addResult(elemId, pName, newValue);

                        // Update the element in the project directly (in-memory)
                        // This ensures that subsequent tool calls relying on element state see the new value
                        // element.parameters[pName] = newValue; // Types might need asserting or casting if map vs obj

                    } catch (e) {
                        console.error(`Failed to evaluate formula for ${pName} on ${elemId}:`, e);
                    }
                }
            }
        }

        return batchResults; // Caller (e.g., BIMKernel) should handle broadcasting/saving
    }



    /**
     * Update a global parameter and propagate changes
     */
    updateGlobalParameter(project: Project, name: string, value: ParameterValue): Map<string, Map<string, ParameterValue>> {
        if (!project.globalParameters) {
            project.globalParameters = new Map();
        }
        project.globalParameters.set(name, value);

        return this.regenerate(project, 'GLOBAL_PROJECT_PARAMS', name);
    }

    /**
     * Calculate reporting (read-only) parameters for an element
     */
    calculateReportingParameters(element: BIMElement): Map<string, ParameterValue> {
        const results = new Map<string, ParameterValue>();

        // Calculate based on geometry
        if ('geometryData' in element) {
            const geom = element.geometryData as any;

            // Length (for linear elements like beams, walls)
            if (geom.start && geom.end) {
                const dx = geom.end[0] - geom.start[0];
                const dy = geom.end[1] - geom.start[1];
                const dz = geom.end[2] - geom.start[2];
                results.set('Length', Math.sqrt(dx * dx + dy * dy + dz * dz));
            }

            // Area (for surface elements like floors, roofs)
            if (geom.boundary && Array.isArray(geom.boundary)) {
                const area = this.calculatePolygonArea(geom.boundary);
                results.set('Area', area);
                results.set('Perimeter', this.calculatePolygonPerimeter(geom.boundary));
            }

            // Volume (for 3D elements)
            if (results.has('Area') && geom.thickness) {
                results.set('Volume', (results.get('Area') as number) * geom.thickness);
            }
        }

        return results;
    }

    private calculatePolygonArea(points: [number, number][]): number {
        let area = 0;
        const n = points.length;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            area += points[i][0] * points[j][1];
            area -= points[j][0] * points[i][1];
        }
        return Math.abs(area) / 2;
    }

    private calculatePolygonPerimeter(points: [number, number][]): number {
        let perimeter = 0;
        const n = points.length;
        for (let i = 0; i < n; i++) {
            const j = (i + 1) % n;
            const dx = points[j][0] - points[i][0];
            const dy = points[j][1] - points[i][1];
            perimeter += Math.sqrt(dx * dx + dy * dy);
        }
        return perimeter;
    }

    /**
     * Get all parameter definitions
     */
    getDefinitions(): ParameterDefinition[] {
        return Array.from(this.definitions.values());
    }

    /**
     * Get parameter definition by name
     */
    getDefinition(name: string): ParameterDefinition | undefined {
        return this.definitions.get(name);
    }
}

// Singleton instance
export const parametricEngine = new ParametricEngine();
